<template>
    <access-denied v-if="accessDenied" title="lps" />
    <div style="height: 100%" v-else>
        <div id="main" v-show="!rotaAninhada">
            <container-loader v-if="isLoading && !$store.getters.isLoading" />
            <div class="page">
                <div class="vertical-flow">
                    <div class="listing-header">
                        <div class="title-breadcrumb">
                            <h1 class="title">
                                {{ $t('lps-title') }}
                                <a class="pl-1" v-if="hide.hideInfo" @click="infoIsHidden()">
                                    <b-icon
                                        v-if="!hide.infoIsHidden"
                                        icon="eye-off-outline"
                                        type="is-danger"
                                    />
                                    <b-icon
                                        v-if="hide.infoIsHidden"
                                        icon="eye-outline"
                                        type="is-success"
                                    />
                                </a>
                            </h1>
                            <nav class="breadcrumb" aria-label="breadcrumbs">
                                <ul>
                                    <li>
                                        <a class="a-inativo">{{ $t('promotor') }}</a>
                                    </li>
                                    <li>
                                        <a class="a-inativo">{{ $t('cadastros') }}</a>
                                    </li>
                                    <li>
                                        <a aria-current="page">{{ $t('lpss') }}</a>
                                    </li>
                                </ul>
                            </nav>
                        </div>
                        <div class="header-buttons">
                            <button
                                v-if="permissao.create && !lpsIntegracao"
                                class="save-btn"
                                type="button"
                                tag="button"
                                @click="$router.push({ name: 'lps-new' })"
                            >
                                {{ $t('adicionar') }}
                            </button>
                        </div>
                    </div>

                    <!-- PESQUISA E FILTROS -->
                    <div class="columns is-multiline">
                        <div class="column is-three-quarters">
                            <search-bar :onSearch="debouncedOnSearch" />
                        </div>

                        <!-- SITUACAO -->
                        <div class="situacao-select column is-one-quarter">
                            <label v-text="$t('situacao')" />
                            <v-select
                                ref="situacaoInput"
                                class="is-100"
                                label="label"
                                :options="situacoes"
                                @input="updateSituacao"
                                v-model="situacao"
                            >
                                <template slot="option" slot-scope="option">
                                    {{ $t(option.label) }}
                                </template>
                                <template slot="selected-option" slot-scope="option">
                                    {{ $t(option.label) }}
                                </template>
                                <template slot="no-options">{{ $t('sem-resultados') }}</template>
                            </v-select>
                        </div>
                    </div>

                    <div class="registry-wrapper">
                        <empty-table-message v-if="isEmpty" />
                        <b-table
                            v-else
                            :data="allLps"
                            class="data-table"
                            @sort="sortTable"
                            backend-sorting
                        >
                            <b-table-column
                                v-for="(f, i) in fields"
                                :key="i"
                                :field="f.key"
                                :label="f.key != 'excluir' ? $t(f.key) : ''"
                                :width="f.width"
                                :class="f.key != 'excluir' ? '' : 'excluir-field'"
                                :sortable="f.sortable"
                                :custom-sort="f.sortFn"
                                v-slot="props"
                            >
                                <div v-if="f.key == 'excluir'">
                                    <b-button
                                        v-if="permissao.delete & !lpsIntegracao"
                                        :type="
                                            props.row['situacao'] == 1
                                                ? 'is-danger'
                                                : 'is-success'
                                        "
                                        @click="promptChangeSituacao(props.row)"
                                        :icon-right="
                                            props.row.situacao == 1 ? 'delete ' : 'check'
                                        "
                                        position="is-left"
                                        v-tippy 
                                        :title="$t(props.row.situacao == 1 ? 'desativar' : 'reativar')"
                                    />
                                </div>
                                <div v-else-if="f.key == 'nome'">
                                    <router-link
                                        :to="{
                                            name: 'lps-edit',
                                            params: { id: props.row.id },
                                        }"
                                    >
                                        <pre>{{ props.row[f.key] }}</pre>
                                    </router-link>
                                </div>
                                <div v-else-if="f.key == 'cnpj'">
                                    <HideInfo
                                        v-if="!cleanHide"
                                        :value="props.row[f.key]"
                                        :hideInfo="hide.hideInfo"
                                        :infoIsHidden="hide.infoIsHidden"
                                        showPartInfoPosition="end"
                                        :showPartInfoLenght="4"
                                    />
                                </div>
                                <div v-else-if="f.key == 'situacao'">
                                    <b-tag
                                        class="table-tag table-tag-full-width"
                                        v-if="props.row[f.key] == 1"
                                        type="is-success"
                                        size="is-medium"
                                        >{{ $t('ativo') }}</b-tag
                                    >
                                    <b-tag
                                        class="table-tag table-tag-full-width"
                                        v-else
                                        type="is-danger"
                                        size="is-medium"
                                        >{{ $t('inativo') }}</b-tag
                                    >
                                </div>
                                <div v-else>
                                    {{ props.row[f.key] }}
                                </div>
                            </b-table-column>
                        </b-table>
                        <PaginationViewer
                            class="mb-0"
                            :resetPagination="pagination.resetPagination"
                            :totalItems="pagination.totalRows"
                            :perPageOptions="pagination.perPageOptions"
                            :optionStarted="pagination.optionStarted"
                            @change="updatePagination"
                        />
                    </div>
                </div>
            </div>
        </div>
        <router-view />
    </div>
</template>

<script>
import ProLpsService from '@/services/promotor/ProLpsService';
import { cpfcnpj } from '@/functions/masks';
import ContainerLoader from '@/components/content/loader/ContainerLoader';
import HideInfo from '@/components/shared/HideInfo';
import { mapState } from 'vuex';

export default {
    beforeRouteUpdate(to, from, next) {
        if (to.name.includes('lps') && to.name != 'lps-list') {
            this.rotaAninhada = true;
        } else {
            this.rotaAninhada = false;
            this.carregarDados(this.pagination.currentPage);
        }
        next();
    },
    components: {
        ContainerLoader,
        HideInfo,
    },
    data() {
        return {
            rotaAninhada: false,
            lpsIntegracao: this.$store.state.user.tabelasERPWebServer.includes('PRO_LPS'),
            cleanHide: true,
            allLps: [],
            situacoes: [
                { id: 2, label: 'todos' },
                { id: 1, label: 'ativos' },
                { id: 0, label: 'inativos' },
            ],
            situacao: { id: 1, label: 'ativos' },
            service: new ProLpsService(this.$http, this.$router),
            fields: [
                { key: 'codigo', width: '15%', sortable: false, sortFn: null },
                { key: 'nome', width: null, sortable: true, sortFn: null },
                {
                    key: 'razaosocial',
                    width: null,
                    sortable: true,
                    sortFn: null,
                },
                { key: 'cnpj', width: 215, sortable: true, sortFn: null },
                { key: 'endereco', width: null, sortable: true, sortFn: null },
                { key: 'situacao', width: 80, sortable: false, sortFn: null },
                { key: 'excluir', width: 20, sortable: false, sortFn: null },
            ],
            isLoading: false,
            isEmpty: false,
            searchQuery: '',
            deleteThis: {},
            pagination: {
                resetPagination: false,
                perPageOptions: [10, 25, 50, 100],
                optionStarted: 0,
                perPage: null,
                totalRows: 0,
                currentPage: 1,
            },
            column: 'nome',
            order: 'asc',
            accessDenied: false,
        };
    },
    created() {
        this.pagination.perPage = this.pagination.perPageOptions[this.pagination.optionStarted];

        this.rotaAninhada = this.$route.name.includes('lps') && this.$route.name != 'lps-list';

        this.permissao = this.hasPermissionAccess('promotor', 'cadastros', 'lpss');
        if (!this.permissao.read) {
            this.accessDenied = true;
            return;
        }

        this.debouncedOnSearch = this.debounce(this.onSearch, 500);
        this.carregarDados();

        this.$on('delete', (value) => {
            this.allowDelete = value.del;
            this.excluir(this.deleteThis);
        });
    },
    computed: {
        ...mapState(['hide']),
    },
    methods: {
        promptChangeSituacao(row) {
            this.delete = row;
            if (row.lpsInRotas > 0 && row.situacao == 1) {
                this.$root.alert({
                    message: this.$t('msg-excluir-lps-rota'),
                    onConfirm: (_) => {
                        this.deleteThis = null;
                    },
                });
            } else if (row.lpsInContratos > 0 && row.situacao == 1) {
                this.$root.alert({
                    message: this.$t('msg-excluir-lps-contrato'),
                    onConfirm: (_) => {
                        this.deleteThis = null;
                    },
                });
            } else {
                let message = this.$t(
                        row.situacao == 1
                            ? 'msg-confirmacao-desativacao-n'
                            : 'msg-confirmacao-reativacao-n', 
                        { n: row.nome }
                    );
                this.$root.confirm({
                    message: message,
                    onConfirm: this.updateSituacaoRegistro,
                    onCancel: () => {
                        this.delete = null;
                    },
                });
            }
        },
        updateSituacaoRegistro() {
            let tempFailure =
                this.delete.situacao == 1
                    ? 'msg-erro-ao-desativar-lps'
                    : 'msg-erro-ao-reativar-lps';

            let tempLps = {
                idlps: this.delete.id,
                situacao: this.delete.situacao == 1 ? 0 : 1,
            };

            this.isLoading = true;

            this.service
                .updateSituacao(tempLps)
                .then((r) => {
                    if (r.status == 200) {
                        let tempSuccess =
                            r.content.situacao == 1
                                ? 'msg-sucesso-ao-reativar-lps'
                                : 'msg-sucesso-ao-desativar-lps';
                        this.$root.showMessage('success', tempSuccess);
                        this.carregarDados(this.pagination.currentPage);
                    } else if (r.status == 500) {
                        this.$root.showMessage('error', 'Erro ao atualizar status.');
                    }
                })
                .catch((e) => {
                    console.error('Erro ao atualizar status.', e);
                    this.$root.showMessage('error', tempFailure);
                })
                .finally((_) => {
                    this.isLoading = false;
                    this.delete = null;
                });
        },
        infoIsHidden() {
            this.$store.dispatch('infoIsHidden', {
                infoIsHidden: !this.hide.infoIsHidden,
            });
        },
        updatePagination(value) {
            this.cleanHide = true;
            this.pagination.resetPagination = false;
            this.pagination.perPage = value.perPage;
            this.pagination.currentPage = value.currentPage;
            this.carregarDados(value.currentPage);
        },
        carregarDados(currentPage = 1) {
            if (currentPage == 1 && this.pagination.currentPage != 1) {
                this.pagination.resetPagination = true;
            }
            this.isLoading = true;
            this.service
                .findAllList(
                    this.column,
                    this.order,
                    currentPage - 1,
                    this.pagination.perPage,
                    this.situacao.id,
                    this.searchQuery
                )
                .then(this.mapItems)
                .then((value) => {
                    this.allLps = value;
                    this.isEmpty = this.allLps.length <= 0;
                })
                .catch((e) => {
                    this.$root.showMessage('error', 'msg-erro-load');
                    console.error('Erro ao carregar', e);
                })
                .finally((_) => {
                    this.pagination.resetPagination = false;
                    this.isLoading = false;
                    this.cleanHide = false;
                });
        },
        updateSituacao(t) {
            if (t) {
                this.situacao = t;
            } else {
                this.situacao = this.situacoes[0];
            }
            this.carregarDados();
        },
        sortTable(field, order) {
            this.cleanHide = true;
            this.column = field;
            this.order = order;
            this.carregarDados();
        },
        onSituacaoChanged(situacao) {
            this.situacao = situacao;
            this.carregarDados();
        },
        mapItems(body) {
            this.pagination.totalRows = body.totalElements;
            let lpslist = [];
            if (body.content) {
                lpslist = body.content.map((l) => {
                    return {
                        id: l.idlps,
                        codigo: l.idlpserp,
                        nome: l.nome,
                        razaosocial: l.rsocial,
                        cnpj: cpfcnpj(l.cnpj),
                        endereco: l.endereco + ', ' + (l.numero ? l.numero : 's/n'),
                        excluir: 'excluir-lps',
                        situacao: l.situacao,
                        lpsInRotas: l.lpsinrotas,
                        lpsInContratos: l.lpsincontratos,
                    };
                });
            }
            return lpslist;
        },
        onSearch(value) {
            this.searchQuery = value
            this.carregarDados();
        },
        confirmExclusion(item) {
            this.deleteThis = item;
            if (item.lpsInRotas > 0) {
                this.$root.alert({
                    message: this.$t('msg-excluir-lps-rota'),
                    onConfirm: (_) => {
                        this.deleteThis = null;
                    },
                });
            } else if (item.lpsInContratos > 0) {
                this.$root.alert({
                    message: this.$t('msg-excluir-lps-contrato'),
                    onConfirm: (_) => {
                        this.deleteThis = null;
                    },
                });
            } else {
                this.$root.confirm({
                    message: this.$t('msg-confirmacao-exclusao-n', { n: item.nome }),
                    onConfirm: this.excluir,
                    onCancel: (_) => {
                        this.deleteThis = null;
                    },
                });
            }
        },
        excluir() {
            let item = this.deleteThis;
            this.isLoading = true;
            this.service
                .delete(item.id)
                .then((r) => {
                    if (r.message) {
                        this.$root.showMessage('error', r.message);
                        this.isLoading = false;
                    } else {
                        this.$root.showMessage('success', 'msg-success-excluir-lps');
                        this.carregarDados(this.pagination.currentPage);
                    }
                })
                .catch((e) => {
                    console.error('Erro ao excluir LPS.', e);
                    if (e.message) {
                        this.$root.showMessage('error', e.message);
                    } else {
                        this.$root.showMessage('error', 'msg-failure-excluir-lps');
                    }
                    this.isLoading = false;
                })
                .finally((_) => {
                    this.deleteThis = null;
                });
        },
    },
};
</script>

<style lang="scss" scoped>
#main {
    padding: 0px 20px;
    height: auto;
    min-height: calc(100% - 50px);
    position: relative;

    .page {
        position: relative;
        padding-left: 0;

        .situacao-select div {
            height: 40px;
        }
    }
}
</style>
